import React, { useState } from 'react';
import { CheckIcon, InfoIcon, LayersIcon } from 'lucide-react';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { EDITION_TYPE_LABELS, type BookEdition, type LinkedStory } from '../../data/linkedEditions';
interface AvailableEditionsProps {
  story: LinkedStory;
  selectedEditionId: string;
  onSelectEdition: (editionId: string) => void;
}

/**
 * Compact edition selector for the book detail page.
 *
 * Deliberately sits ABOVE the fold-level metadata it controls, because
 * selecting an edition changes the cover, description, warnings, spice,
 * darkness, ending and retailer links below it — a reader needs to know the
 * choice exists before they read values that depend on it.
 *
 * Renders nothing when a story has one edition; the caller also guards, so a
 * single-edition book looks exactly as it did before.
 */
export const AvailableEditions: React.FC<AvailableEditionsProps> = ({
  story,
  selectedEditionId,
  onSelectEdition
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const [showDifference, setShowDifference] = useState(false);
  if (story.editions.length < 2) return null;
  const selected = story.editions.find(edition => edition.id === selectedEditionId) || story.editions[0];
  return <section aria-labelledby="available-editions" className={`rounded-xl border p-4 sm:p-5 mb-6 ${dk.border} ${dk.surface}`} data-id="element-7120" data-mp="1797ff9769una">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1" data-id="element-7121" data-mp="y9u92pechm6s">
        <div className="flex items-center gap-2" data-id="element-7122" data-mp="y7r9bdne4eov">
          <LayersIcon className={`h-4 w-4 ${rt.textPrimary}`} data-id="element-7123" data-mp="jdwfnevt2vy9" />
          <h2 id="available-editions" className={`text-sm font-bold ${dk.textHeading}`} data-id="element-7124" data-mp="obo604gsya0c">
            Available Editions
          </h2>
          <span className={`text-xs ${dk.textMuted}`} data-id="element-7125" data-mp="2dorngez626zz">
            {story.editions.length} versions of this story
          </span>
        </div>
        <button onClick={() => setShowDifference(open => !open)} aria-expanded={showDifference} className={`inline-flex items-center gap-1.5 text-xs font-medium ${rt.accentLink}`} data-id="element-7126" data-mp="2h9bee17s1xyj">
          <InfoIcon className="h-3.5 w-3.5" data-id="element-7127" data-mp="27bdnf5yj9n28" />
          What&apos;s different?
        </button>
      </div>

      <p className={`text-xs mb-4 ${dk.textSecondary}`} data-id="element-7128" data-mp="23ale6cpnvi5y">
        Same story, different intensity. Choose the version that suits you —
        your selection updates the details below.
      </p>

      {/* Edition cards */}
      <div role="radiogroup" aria-label="Choose an edition" className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-7129" data-mp="2v29mmo5bjkji">
        {story.editions.map(edition => {
        const isSelected = edition.id === selected.id;
        return <button key={edition.id} role="radio" aria-checked={isSelected} onClick={() => onSelectEdition(edition.id)} className={`flex gap-3 text-left p-3 rounded-lg border transition-colors ${isSelected ? `${rt.borderActive} ${rt.bgLight}` : `${dk.border} ${dk.surfaceMuted}`}`} data-id="element-7130" data-mp="3iqkite02kw1g">
              <img src={edition.coverImage} alt={`${story.title} — ${edition.name} cover`} className="w-14 h-20 object-cover rounded flex-shrink-0 shadow-sm" data-id="element-7131" data-mp="19j7cngvgxgxg" />
              <span className="min-w-0 flex-1" data-id="element-7132" data-mp="1oedfjuz9ozmk">
                <span className="flex items-center gap-1.5 mb-0.5" data-id="element-7133" data-mp="1od2cxyhk5xx1">
                  <span className={`text-sm font-semibold truncate ${dk.textHeading}`} data-id="element-7134" data-mp="1t4k825wwh4a3">
                    {edition.name}
                  </span>
                  {isSelected && <CheckIcon className={`h-3.5 w-3.5 flex-shrink-0 ${rt.textPrimary}`} data-id="element-7135" data-mp="22734a766kind" />}
                </span>
                <span className={`block text-xs mb-1.5 ${dk.textMuted}`} data-id="element-7136" data-mp="1thmin2z19b6i">
                  {EDITION_TYPE_LABELS[edition.type]}
                </span>
                <span className="flex flex-wrap gap-1" data-id="element-7137" data-mp="1ogyr8g6w6sbr">
                  {edition.editionTags.map(tag => <span key={tag} className={`inline-block px-1.5 py-0.5 text-[11px] rounded-full ${dk.badgeNeutral}`} data-id="element-7138" data-mp="239s55x6in0eb">
                      {tag}
                    </span>)}
                </span>
              </span>
            </button>;
      })}
      </div>

      {/* Explanation, expanded on demand so it never crowds the selector */}
      {showDifference && <div className={`mt-4 pt-4 border-t ${dk.borderSubtle}`} data-id="element-7139" data-mp="1fhjy18zcc3nk">
          <h3 className={`text-xs font-semibold mb-2 ${dk.textHeading}`} data-id="element-7140" data-mp="2vw3f3yg0idns">
            How these editions differ
          </h3>
          <dl className="space-y-2" data-id="element-7141" data-mp="2qwqkusmta4b6">
            {story.editions.map(edition => <div key={edition.id} data-id="element-7142" data-mp="2yfzt2ijkzwyn">
                <dt className={`text-xs font-medium ${dk.textPrimary}`} data-id="element-7143" data-mp="2yj44o0zdluzc">
                  {edition.name}
                </dt>
                <dd className={`text-xs leading-relaxed ${dk.textSecondary}`} data-id="element-7144" data-mp="2pgmi1lft70bt">
                  {edition.whatsDifferent}
                </dd>
              </div>)}
          </dl>
        </div>}

      {/* Always-visible summary of the current choice */}
      <p className={`mt-3 text-xs leading-relaxed ${dk.textSecondary}`} aria-live="polite" data-id="element-7145" data-mp="3h9lvu14k8iil">
        <span className={`font-medium ${dk.textPrimary}`} data-id="element-7146" data-mp="3m8gf146fw938">
          You&apos;re viewing the {selected.name}:
        </span>{' '}
        {selected.whatsDifferent}
      </p>
    </section>;
};
export default AvailableEditions;